import {
  BoxGeometry,
  CanvasTexture,
  DoubleSide,
  Group,
  LinearFilter,
  LOD,
  Material,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  PlaneGeometry,
  SRGBColorSpace,
  Texture,
  Vector3,
} from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";

import {
  PUBLIC_TRANSPORT_TUNING,
  PUBLIC_TRANSPORT_VEHICLE_ASSETS,
  type PublicTransportVehicleAssetSpec,
} from "../../config/publicTransport.ts";
import type { PublicTransportVehiclePresentation } from "../../sim/publicTransport/index.ts";
import type { VilniusPublicTransportMode } from "../../world/busStops.ts";

const TRANSPORT_MODES = ["bus", "trolleybus"] as const;
const ROUTE_DISPLAY_CANVAS_WIDTH = 960;
const ROUTE_DISPLAY_CANVAS_HEIGHT = 200;
const ROUTE_DISPLAY_Y_M = 2.79;
const SIDE_ROUTE_DISPLAY_Y_M = 2.66;
const DOOR_RECESS_Y_M = 1.43;
// Drivable vehicles receive a source-level half turn before export, so the
// authored Blender +Y nose is runtime +Z. Keep these in the rendered GLB frame.
const DOOR_RECESS_Z_M = [3.6, -0.1, -4.08] as const;
const DOOR_SLIDE_M = 0.47;
const DOOR_OUTWARD_M = 0.055;
const LOD_HYSTERESIS = 0.1;

export interface PublicTransportAssetLoader {
  loadAsync(url: string): Promise<Readonly<{ scene: Group }>>;
}

export interface PublicTransportFleetDiagnostics {
  readonly loadedModeCount: number;
  readonly sourceMeshCount: number;
  readonly sourceTriangleCount: number;
  readonly trolleyPoleNodeCount: number;
  readonly maximumRenderedVehicles: number;
  activeVehicleCount: number;
  activeBusCount: number;
  activeTrolleybusCount: number;
  routeDisplayCount: number;
  culledPresentationCount: number;
}

export interface PublicTransportFleetView {
  readonly object: Group;
  readonly diagnostics: PublicTransportFleetDiagnostics;
  setPresentations(
    presentations: readonly Readonly<PublicTransportVehiclePresentation>[],
  ): void;
  dispose(): void;
}

interface AssetLevel {
  readonly scene: Group;
  readonly meshCount: number;
  readonly triangleCount: number;
  readonly trolleyPoleNodeCount: number;
}

interface ModeAssets {
  readonly spec: PublicTransportVehicleAssetSpec;
  readonly full: AssetLevel;
  readonly simplified: AssetLevel;
}

interface AnimatedDoor {
  readonly object: Object3D;
  readonly basePosition: Vector3;
  readonly slideDirection: -1 | 1;
}

interface RouteDisplayVisual {
  readonly texture: CanvasTexture;
  readonly material: MeshBasicMaterial;
  readonly meshes: readonly Mesh<PlaneGeometry, MeshBasicMaterial>[];
}

interface PublicTransportVisual {
  readonly object: Group;
  readonly mode: VilniusPublicTransportMode;
  readonly identityKey: string;
  readonly doors: readonly AnimatedDoor[];
  readonly doorRecesses: readonly Mesh<BoxGeometry, MeshStandardMaterial>[];
  readonly routeDisplay: RouteDisplayVisual;
}

function configureAssetLevel(
  scene: Group,
  castShadow: boolean,
): Omit<AssetLevel, "scene"> {
  let meshCount = 0;
  let triangleCount = 0;
  let trolleyPoleNodeCount = 0;
  scene.traverse((child) => {
    if (/trolley-pole|pole-(?:pivot|shoe)/u.test(child.name.toLowerCase())) {
      trolleyPoleNodeCount += 1;
    }
    if (!(child instanceof Mesh)) return;
    meshCount += 1;
    child.castShadow = castShadow;
    child.receiveShadow = true;
    const position = child.geometry.getAttribute("position");
    triangleCount += Math.floor(
      (child.geometry.index?.count ?? position?.count ?? 0) / 3,
    );
  });
  return { meshCount, triangleCount, trolleyPoleNodeCount };
}

function disposeObjectResources(object: Object3D): void {
  const geometries = new Set<{ dispose(): void }>();
  const materials = new Set<Material>();
  const textures = new Set<Texture>();
  object.traverse((child) => {
    if (!(child instanceof Mesh)) return;
    geometries.add(child.geometry);
    for (const material of Array.isArray(child.material)
      ? child.material
      : [child.material]) {
      materials.add(material);
      for (const value of Object.values(material)) {
        if (value instanceof Texture) textures.add(value);
      }
    }
  });
  for (const geometry of geometries) geometry.dispose();
  for (const texture of textures) texture.dispose();
  for (const material of materials) material.dispose();
  object.removeFromParent();
  object.clear();
}

async function loadModeAssets(
  mode: VilniusPublicTransportMode,
  loader: PublicTransportAssetLoader,
): Promise<ModeAssets> {
  const spec = PUBLIC_TRANSPORT_VEHICLE_ASSETS[mode];
  const levels = await Promise.allSettled([
    loader.loadAsync(spec.assetUrl),
    loader.loadAsync(spec.lod1Url),
  ]);
  const failure = levels.find(
    (level): level is PromiseRejectedResult => level.status === "rejected",
  );
  if (failure !== undefined) {
    for (const level of levels) {
      if (level.status === "fulfilled") {
        disposeObjectResources(level.value.scene);
      }
    }
    throw failure.reason;
  }
  const fullResult = levels[0];
  const simplifiedResult = levels[1];
  if (
    fullResult?.status !== "fulfilled" ||
    simplifiedResult?.status !== "fulfilled"
  ) {
    throw new Error(`Could not load both public-transport levels for ${mode}`);
  }
  const fullScene = fullResult.value.scene;
  const simplifiedScene = simplifiedResult.value.scene;
  return {
    spec,
    full: {
      scene: fullScene,
      ...configureAssetLevel(fullScene, true),
    },
    simplified: {
      scene: simplifiedScene,
      ...configureAssetLevel(simplifiedScene, false),
    },
  };
}

function normalizeRouteColour(value: string, fallback: string): string {
  const colour = value.trim().replace(/^#/u, "");
  return /^[0-9a-f]{6}$/iu.test(colour) ? `#${colour}` : fallback;
}

function fitCanvasText(
  context: CanvasRenderingContext2D,
  text: string,
  maximumWidth: number,
  maximumFontSize: number,
  minimumFontSize: number,
  weight: number,
): number {
  for (
    let fontSize = maximumFontSize;
    fontSize >= minimumFontSize;
    fontSize -= 2
  ) {
    context.font = `${weight} ${fontSize}px system-ui, sans-serif`;
    if (context.measureText(text).width <= maximumWidth) return fontSize;
  }
  return minimumFontSize;
}

function createRouteDisplayTexture(
  presentation: Readonly<PublicTransportVehiclePresentation>,
): CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = ROUTE_DISPLAY_CANVAS_WIDTH;
  canvas.height = ROUTE_DISPLAY_CANVAS_HEIGHT;
  const context = canvas.getContext("2d");
  if (context === null) {
    throw new Error("Could not create the public-transport route display");
  }
  const background = normalizeRouteColour(
    presentation.routeColor,
    presentation.mode === "trolleybus" ? "#006cb7" : "#0057a8",
  );
  const foreground = normalizeRouteColour(
    presentation.routeTextColor,
    "#ffffff",
  );
  const headsign = presentation.headsign
    .trim()
    .slice(0, PUBLIC_TRANSPORT_TUNING.maximumHeadsignCharacters);
  const routeNumber = presentation.routeShortName.trim();

  context.fillStyle = background;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.strokeStyle = "rgba(255,255,255,0.34)";
  context.lineWidth = 7;
  context.strokeRect(4, 4, canvas.width - 8, canvas.height - 8);
  context.fillStyle = foreground;
  context.textBaseline = "middle";
  context.textAlign = "center";
  const numberWidth = 218;
  const numberSize = fitCanvasText(
    context,
    routeNumber,
    numberWidth - 24,
    128,
    72,
    850,
  );
  context.font = `850 ${numberSize}px system-ui, sans-serif`;
  context.fillText(routeNumber, numberWidth / 2, canvas.height / 2 + 2);
  context.fillStyle = "rgba(255,255,255,0.35)";
  context.fillRect(numberWidth, 26, 5, canvas.height - 52);
  context.fillStyle = foreground;
  context.textAlign = "left";
  const headsignSize = fitCanvasText(
    context,
    headsign,
    canvas.width - numberWidth - 54,
    66,
    34,
    650,
  );
  context.font = `650 ${headsignSize}px system-ui, sans-serif`;
  context.fillText(headsign, numberWidth + 31, canvas.height / 2 + 2);

  const texture = new CanvasTexture(canvas);
  texture.name = `public-transport-route:${presentation.sourceRouteId}:${presentation.headsign}`;
  texture.colorSpace = SRGBColorSpace;
  texture.minFilter = LinearFilter;
  texture.magFilter = LinearFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return texture;
}

function createRouteDisplay(
  presentation: Readonly<PublicTransportVehiclePresentation>,
  spec: Readonly<PublicTransportVehicleAssetSpec>,
): RouteDisplayVisual {
  const texture = createRouteDisplayTexture(presentation);
  const material = new MeshBasicMaterial({
    name: `public-transport-route-display:${presentation.sourceRouteId}`,
    map: texture,
    side: DoubleSide,
    toneMapped: false,
  });
  const front = new Mesh(
    new PlaneGeometry(
      PUBLIC_TRANSPORT_TUNING.routeDisplayWidthM,
      PUBLIC_TRANSPORT_TUNING.routeDisplayHeightM,
    ),
    material,
  );
  front.name = `public-transport-front-display:${presentation.id}`;
  front.position.set(
    0,
    ROUTE_DISPLAY_Y_M,
    spec.dimensionsM.z / 2 +
      PUBLIC_TRANSPORT_TUNING.routeDisplaySurfaceOffsetM +
      0.16,
  );
  front.renderOrder = 1;

  const side = new Mesh(
    new PlaneGeometry(
      PUBLIC_TRANSPORT_TUNING.routeDisplayWidthM,
      PUBLIC_TRANSPORT_TUNING.routeDisplayHeightM,
    ),
    material,
  );
  side.name = `public-transport-side-display:${presentation.id}`;
  side.position.set(
    spec.dimensionsM.x / 2 +
      PUBLIC_TRANSPORT_TUNING.routeDisplaySurfaceOffsetM +
      0.02,
    SIDE_ROUTE_DISPLAY_Y_M,
    1.8,
  );
  side.rotation.y = Math.PI / 2;
  side.renderOrder = 1;
  return { texture, material, meshes: [front, side] };
}

function collectDoors(root: Object3D): readonly AnimatedDoor[] {
  const doors: AnimatedDoor[] = [];
  root.traverse((child) => {
    const match = /^passenger-door-(\d+)$/u.exec(child.name.toLowerCase());
    if (match === null) return;
    const index = Number.parseInt(match[1] ?? "0", 10);
    doors.push({
      object: child,
      basePosition: child.position.clone(),
      slideDirection: index === 1 ? 1 : -1,
    });
  });
  return doors;
}

function createDoorRecesses(
  spec: Readonly<PublicTransportVehicleAssetSpec>,
): readonly Mesh<BoxGeometry, MeshStandardMaterial>[] {
  const geometry = new BoxGeometry(0.035, 1.78, 1.12);
  const material = new MeshStandardMaterial({
    name: "public-transport-open-door-recess",
    color: 0x111817,
    roughness: 0.92,
    metalness: 0,
  });
  return DOOR_RECESS_Z_M.map((z, index) => {
    const recess = new Mesh(geometry, material);
    recess.name = `public-transport-open-door:${index}`;
    recess.position.set(
      spec.dimensionsM.x / 2 + 0.012,
      DOOR_RECESS_Y_M,
      z,
    );
    recess.visible = false;
    recess.receiveShadow = true;
    return recess;
  });
}

function visualIdentityKey(
  presentation: Readonly<PublicTransportVehiclePresentation>,
): string {
  return [
    presentation.mode,
    presentation.sourceRouteId,
    presentation.routeShortName,
    presentation.headsign,
    presentation.routeColor,
    presentation.routeTextColor,
  ].join("\u0000");
}

function disposeVisual(visual: Readonly<PublicTransportVisual>): void {
  visual.object.removeFromParent();
  for (const mesh of visual.routeDisplay.meshes) mesh.geometry.dispose();
  visual.routeDisplay.texture.dispose();
  visual.routeDisplay.material.dispose();
  const doorGeometries = new Set(
    visual.doorRecesses.map((recess) => recess.geometry),
  );
  const doorMaterials = new Set(
    visual.doorRecesses.map((recess) => recess.material),
  );
  for (const geometry of doorGeometries) geometry.dispose();
  for (const material of doorMaterials) material.dispose();
  visual.object.clear();
}

function updateVisual(
  visual: Readonly<PublicTransportVisual>,
  presentation: Readonly<PublicTransportVehiclePresentation>,
): void {
  visual.object.position.set(
    presentation.position.x,
    presentation.position.y,
    presentation.position.z,
  );
  visual.object.rotation.y = presentation.yawRadians;
  const doorOpen = presentation.doorsOpen ? 1 : 0;
  for (const door of visual.doors) {
    door.object.position.copy(door.basePosition);
    door.object.position.x += DOOR_OUTWARD_M * doorOpen;
    door.object.position.z += DOOR_SLIDE_M * door.slideDirection * doorOpen;
  }
  for (const recess of visual.doorRecesses) {
    recess.visible = presentation.doorsOpen;
  }
  visual.object.userData = {
    publicTransportVehicleId: presentation.id,
    patternId: presentation.patternId,
    sourceRouteId: presentation.sourceRouteId,
    routeShortName: presentation.routeShortName,
    headsign: presentation.headsign,
    mode: presentation.mode,
    doorsOpen: presentation.doorsOpen,
    occupied: presentation.occupied,
  };
}

export async function loadPublicTransportFleetView(
  loader: PublicTransportAssetLoader = new GLTFLoader(),
): Promise<PublicTransportFleetView> {
  const loaded = await Promise.allSettled(
    TRANSPORT_MODES.map((mode) => loadModeAssets(mode, loader)),
  );
  const failed = loaded.find(
    (result): result is PromiseRejectedResult => result.status === "rejected",
  );
  if (failed !== undefined) {
    for (const result of loaded) {
      if (result.status !== "fulfilled") continue;
      disposeObjectResources(result.value.full.scene);
      disposeObjectResources(result.value.simplified.scene);
    }
    throw failed.reason;
  }
  const assetsByMode = new Map<VilniusPublicTransportMode, ModeAssets>();
  for (const result of loaded) {
    if (result.status === "fulfilled") {
      assetsByMode.set(result.value.spec.mode, result.value);
    }
  }
  if (assetsByMode.size !== TRANSPORT_MODES.length) {
    throw new Error("Public-transport fleet assets are incomplete");
  }

  const object = new Group();
  object.name = "vilnius-public-transport-fleet";
  const visuals = new Map<string, PublicTransportVisual>();
  const levels = [...assetsByMode.values()].flatMap((asset) => [
    asset.full,
    asset.simplified,
  ]);
  const diagnostics: PublicTransportFleetDiagnostics = {
    loadedModeCount: assetsByMode.size,
    sourceMeshCount: levels.reduce((sum, level) => sum + level.meshCount, 0),
    sourceTriangleCount: levels.reduce(
      (sum, level) => sum + level.triangleCount,
      0,
    ),
    trolleyPoleNodeCount: levels.reduce(
      (sum, level) => sum + level.trolleyPoleNodeCount,
      0,
    ),
    maximumRenderedVehicles: PUBLIC_TRANSPORT_TUNING.maximumRenderedVehicles,
    activeVehicleCount: 0,
    activeBusCount: 0,
    activeTrolleybusCount: 0,
    routeDisplayCount: 0,
    culledPresentationCount: 0,
  };

  function createVisual(
    presentation: Readonly<PublicTransportVehiclePresentation>,
  ): PublicTransportVisual {
    const assets = assetsByMode.get(presentation.mode);
    if (assets === undefined) {
      throw new Error(`Missing public-transport asset for ${presentation.mode}`);
    }
    const group = new Group();
    group.name = `public-transport:${presentation.id}`;
    const lod = new LOD();
    lod.name = `public-transport-lod:${presentation.id}`;
    const full = assets.full.scene.clone(true);
    const simplified = assets.simplified.scene.clone(true);
    lod.addLevel(full, 0);
    lod.addLevel(simplified, assets.spec.lodDistanceM, LOD_HYSTERESIS);
    group.add(lod);
    const routeDisplay = createRouteDisplay(presentation, assets.spec);
    group.add(...routeDisplay.meshes);
    const doorRecesses = createDoorRecesses(assets.spec);
    group.add(...doorRecesses);
    const visual: PublicTransportVisual = {
      object: group,
      mode: presentation.mode,
      identityKey: visualIdentityKey(presentation),
      doors: [...collectDoors(full), ...collectDoors(simplified)],
      doorRecesses,
      routeDisplay,
    };
    object.add(group);
    visuals.set(presentation.id, visual);
    return visual;
  }

  let disposed = false;
  return {
    object,
    diagnostics,
    setPresentations(presentations): void {
      if (disposed) return;
      const accepted = new Map<
        string,
        Readonly<PublicTransportVehiclePresentation>
      >();
      for (const presentation of presentations) {
        if (
          accepted.size >= PUBLIC_TRANSPORT_TUNING.maximumRenderedVehicles
        ) {
          break;
        }
        if (!accepted.has(presentation.id)) {
          accepted.set(presentation.id, presentation);
        }
      }
      diagnostics.culledPresentationCount = Math.max(
        0,
        presentations.length - accepted.size,
      );
      for (const [id, visual] of visuals) {
        if (accepted.has(id)) continue;
        disposeVisual(visual);
        visuals.delete(id);
      }
      for (const presentation of accepted.values()) {
        let visual = visuals.get(presentation.id);
        const identityKey = visualIdentityKey(presentation);
        if (visual !== undefined && visual.identityKey !== identityKey) {
          disposeVisual(visual);
          visuals.delete(presentation.id);
          visual = undefined;
        }
        visual ??= createVisual(presentation);
        updateVisual(visual, presentation);
      }
      diagnostics.activeVehicleCount = accepted.size;
      diagnostics.activeBusCount = [...accepted.values()].filter(
        (presentation) => presentation.mode === "bus",
      ).length;
      diagnostics.activeTrolleybusCount =
        diagnostics.activeVehicleCount - diagnostics.activeBusCount;
      diagnostics.routeDisplayCount = diagnostics.activeVehicleCount * 2;
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      for (const visual of visuals.values()) disposeVisual(visual);
      visuals.clear();
      for (const assets of assetsByMode.values()) {
        disposeObjectResources(assets.full.scene);
        disposeObjectResources(assets.simplified.scene);
      }
      assetsByMode.clear();
      diagnostics.activeVehicleCount = 0;
      diagnostics.activeBusCount = 0;
      diagnostics.activeTrolleybusCount = 0;
      diagnostics.routeDisplayCount = 0;
      object.removeFromParent();
      object.clear();
    },
  };
}
